import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

/**
 * GoogleOAuthCallback
 *
 * Rendered at /auth/google/callback after Google redirects back with
 * an authorization code. Exchanges the code for tokens via the backend,
 * then redirects to the main app.
 *
 * WHY inline styles: GeoAgent frontend uses inline styles for newer
 * components (no Tailwind dependency needed for this standalone page).
 */
const GoogleOAuthCallback = () => {
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    // Guard against React StrictMode's double-invocation of effects in development.
    // Authorization codes are single-use — a second exchange attempt would fail with
    // invalid_grant even though the first succeeded, causing a false "failed" flash.
    const exchangeAttempted = useRef(false);

    useEffect(() => {
        const exchangeCode = async () => {
            if (exchangeAttempted.current) return;
            exchangeAttempted.current = true;
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            const state = params.get('state');

            if (!code || !state) {
                setStatus('error');
                setErrorMessage('Missing authorization code or state parameter.');
                return;
            }

            try {
                await axios.post(
                    `${API_BASE_URL}/api/auth/google/callback`,
                    { code, state },
                    { withCredentials: true }
                );
                setStatus('success');

                // Redirect to main app after brief success message
                setTimeout(() => {
                    window.location.href = '/';
                }, 2000);
            } catch (err) {
                setStatus('error');
                const detail = err.response?.data?.detail || 'Failed to connect Google Calendar.';
                setErrorMessage(detail);
            }
        };

        exchangeCode();
    }, []);

    const containerStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px',
    };

    const cardStyle = {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
        textAlign: 'center',
        maxWidth: '420px',
        width: '100%',
    };

    const iconStyle = {
        fontSize: '48px',
        marginBottom: '16px',
    };

    const titleStyle = {
        fontSize: '20px',
        fontWeight: '700',
        color: '#111827',
        marginBottom: '8px',
    };

    const subtitleStyle = {
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '24px',
    };

    const linkStyle = {
        display: 'inline-block',
        padding: '10px 24px',
        backgroundColor: '#3b82f6',
        color: '#fff',
        borderRadius: '8px',
        textDecoration: 'none',
        fontWeight: '600',
        fontSize: '14px',
    };

    const spinnerStyle = {
        width: '40px',
        height: '40px',
        border: '4px solid #e5e7eb',
        borderTopColor: '#3b82f6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        marginBottom: '16px',
    };

    return (
        <div style={containerStyle}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={cardStyle}>
                {status === 'loading' && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={spinnerStyle} />
                        </div>
                        <div style={titleStyle}>Connecting Google Calendar...</div>
                        <div style={subtitleStyle}>Please wait while we complete the authorization.</div>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div style={iconStyle}>&#x2705;</div>
                        <div style={titleStyle}>Google Calendar Connected!</div>
                        <div style={subtitleStyle}>Redirecting you back to the app...</div>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div style={iconStyle}>&#x274C;</div>
                        <div style={titleStyle}>Connection Failed</div>
                        <div style={subtitleStyle}>{errorMessage}</div>
                        <a href="/" style={linkStyle}>Return to App</a>
                    </>
                )}
            </div>
        </div>
    );
};

export default GoogleOAuthCallback;
